import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "./config";

const PRODUCTS_COLLECTION = "products";
const CATEGORIES_COLLECTION = "categories";

const getProductImageUrls = (productData = {}) => {
  const imageUrls = new Set();

  if (typeof productData.image === "string" && productData.image.trim()) {
    imageUrls.add(productData.image);
  }

  if (Array.isArray(productData.images)) {
    productData.images.forEach((url) => {
      if (typeof url === "string" && url.trim()) {
        imageUrls.add(url);
      }
    });
  }

  return [...imageUrls];
};

// Upload image to Firebase Storage
export const uploadImage = async (file, folder = "products") => {
  const timestamp = Date.now();
  const fileName = `${folder}/${timestamp}-${file.name}`;
  const storageRef = ref(storage, fileName);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  return { url: downloadURL, path: fileName };
};

// Delete image from Firebase Storage
export const deleteImage = async (imagePathOrUrl) => {
  try {
    const imageRef = ref(storage, imagePathOrUrl);
    await deleteObject(imageRef);
  } catch (error) {
    if (error.code !== "storage/object-not-found") {
      console.error("Error deleting image:", error);
    }
  }
};

export const deleteImagesByUrls = async (imageUrls = []) => {
  const uniqueUrls = [...new Set(imageUrls)].filter(
    (url) => typeof url === "string" && url.trim(),
  );

  await Promise.all(uniqueUrls.map((url) => deleteImage(url)));
};

// Add a new product
export const addProduct = async (productData) => {
  const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), {
    ...productData,
    createdAt: new Date().toISOString(),
  });
  return { id: docRef.id, ...productData };
};

// Get all products
export const getProducts = async () => {
  const q = query(
    collection(db, PRODUCTS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

// Update a product
export const updateProduct = async (productId, productData) => {
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  await updateDoc(productRef, {
    ...productData,
    updatedAt: new Date().toISOString(),
  });
};

// Delete a product
export const deleteProduct = async (productId) => {
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  const productSnapshot = await getDoc(productRef);

  if (productSnapshot.exists()) {
    const imageUrls = getProductImageUrls(productSnapshot.data());
    await deleteImagesByUrls(imageUrls);
  }

  await deleteDoc(productRef);
};

// Get all categories
export const getCategories = async () => {
  const querySnapshot = await getDocs(collection(db, CATEGORIES_COLLECTION));
  if (querySnapshot.empty) {
    // Return default categories if none exist in Firestore
    return [
      { id: "ghee-and-oil", name: "Ghee and Oil", icon: "🧈" },
      { id: "beverages", name: "Beverages", icon: "🥤" },
      { id: "malt-and-health-mix", name: "Malt and Health Mix", icon: "🥣" },
      { id: "palm-products", name: "Palm Products", icon: "🌴" },
      { id: "rice-millets", name: "Rice Millets", icon: "🌾" },
      { id: "snacks", name: "Snacks", icon: "🍿" },
      { id: "tiffin", name: "Tiffin", icon: "🍽️" },
    ];
  }
  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};
